import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';

const STATE = {
  VALIDATING: 'validating',
  READY:      'ready',
  UPLOADING:  'uploading',
  SUCCESS:    'success',
  EXPIRED:    'expired',
  INVALID:    'invalid',
  ERROR:      'error',
};

export default function MobileScan() {
  const { token } = useParams();
  const [state,   setState]   = useState(STATE.VALIDATING);
  const [preview, setPreview] = useState(null);
  const [file,    setFile]    = useState(null);
  const inputRef = useRef(null);

  // Validate the token on load
  useEffect(() => {
    async function validate() {
      try {
        const snap = await getDoc(doc(db, 'scanSessions', token));
        if (!snap.exists()) { setState(STATE.INVALID); return; }

        const data = snap.data();
        if (data.status !== 'pending') { setState(STATE.EXPIRED); return; }

        const expiresAt = data.expiresAt?.toDate?.();
        if (expiresAt && expiresAt < new Date()) { setState(STATE.EXPIRED); return; }

        setState(STATE.READY);
      } catch {
        setState(STATE.INVALID);
      }
    }
    validate();
  }, [token]);

  function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }

  async function handleUpload() {
    if (!file) return;
    setState(STATE.UPLOADING);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const storagePath = `scan-sessions/${token}/receipt.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type });
      // Write storagePath to Firestore so the desktop picks it up via onSnapshot
      await updateDoc(doc(db, 'scanSessions', token), {
        status: 'uploaded',
        storagePath,
      });
      setState(STATE.SUCCESS);
    } catch (err) {
      console.error('Upload failed:', err);
      setState(STATE.ERROR);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-5 py-10">

      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-[10px] bg-[#1e3a5f] flex items-center justify-center">
          <span className="text-white font-bold text-[13px] tracking-wide">MST</span>
        </div>
        <span className="text-[#1e3a5f] font-bold text-[18px]">Receipt Scan</span>
      </div>

      <div className="w-full max-w-sm bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm p-6">

        {/* Validating */}
        {state === STATE.VALIDATING && (
          <div className="text-center py-8">
            <svg className="w-8 h-8 animate-spin text-[#1e3a5f] mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-[14px] text-[#6B7280]">Checking link…</p>
          </div>
        )}

        {/* Invalid token */}
        {state === STATE.INVALID && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#111827] mb-1">Invalid link</p>
            <p className="text-[13px] text-[#6B7280]">This QR code is not recognised. Please go back to the portal and try again.</p>
          </div>
        )}

        {/* Expired token */}
        {state === STATE.EXPIRED && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#111827] mb-1">Link expired</p>
            <p className="text-[13px] text-[#6B7280]">This QR code has expired or already been used. Go back to the portal and click Scan Receipt again to get a new one.</p>
          </div>
        )}

        {/* Ready to capture */}
        {state === STATE.READY && (
          <>
            <p className="text-[15px] font-semibold text-[#111827] mb-1">Upload your receipt</p>
            <p className="text-[13px] text-[#6B7280] mb-5">Take a photo or choose an image from your camera roll.</p>

            {/* Preview */}
            {preview ? (
              <div className="mb-5">
                <img src={preview} alt="Receipt preview" className="w-full rounded-[10px] object-contain max-h-[280px] border border-[#E5E7EB]" />
                <button
                  onClick={() => { setFile(null); setPreview(null); inputRef.current.value = ''; }}
                  className="mt-2 text-[12px] text-[#6B7280] hover:text-[#374151] underline"
                >
                  Choose a different photo
                </button>
              </div>
            ) : (
              <button
                onClick={() => inputRef.current?.click()}
                className="w-full border-2 border-dashed border-[#D1D5DB] rounded-[12px] py-10 flex flex-col items-center gap-2 text-[#6B7280] hover:border-[#1e3a5f]/40 hover:text-[#1e3a5f] transition mb-5"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-[14px] font-medium">Take photo / choose image</span>
              </button>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />

            {file && (
              <button
                onClick={handleUpload}
                className="w-full bg-[#1e3a5f] hover:bg-[#152d4a] text-white font-semibold text-[15px] py-3 rounded-[10px] transition"
              >
                Send to laptop
              </button>
            )}
          </>
        )}

        {/* Uploading */}
        {state === STATE.UPLOADING && (
          <div className="text-center py-8">
            <svg className="w-8 h-8 animate-spin text-[#1e3a5f] mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-[14px] text-[#6B7280]">Sending receipt…</p>
          </div>
        )}

        {/* Success */}
        {state === STATE.SUCCESS && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-[16px] font-bold text-[#111827] mb-1">Receipt sent!</p>
            <p className="text-[13px] text-[#6B7280]">The form on your laptop is filling in automatically. You can close this page.</p>
          </div>
        )}

        {/* Error */}
        {state === STATE.ERROR && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#111827] mb-2">Upload failed</p>
            <p className="text-[13px] text-[#6B7280] mb-4">Something went wrong. Please try again.</p>
            <button
              onClick={() => setState(STATE.READY)}
              className="text-[13px] text-[#1e3a5f] font-semibold underline"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      <p className="text-[11px] text-[#9CA3AF] mt-6">MST Sports Tours · Secure receipt upload</p>
    </div>
  );
}
